// src/components/HealthProfileForm.jsx
//
// Shared health profile form — used by:
//   PatientSettingsPage  → "Health Profile" section (editable any time)
//   PatientOnboardingPage → RiskIntakeStep (first-time capture)
//
// Constitution §VIII: "Risk stratification and symptom capture are clinical inputs
// that modify threshold sensitivity, pathway activation, and monitoring cadence
// across the entire system."
//
// Data is persisted via PUT /api/patients/{id} as a structured string in
// medicalHistory: "Conditions: X, Y. Family history: A, B."
// allergies and currentMedications are stored as their own fields.
//
// Props:
//   patient        {id, ...}  — from usePatient()
//   initialData    object     — optional; pre-populate from existing patient record
//   onSave         fn(data)   — called after successful API save with the saved data
//   onSkip         fn()       — optional; renders a skip link (for onboarding use)
//   onBack         fn()       — optional; renders a Back button (for onboarding use)
//   saveLabel      string     — button label (default: "Save health profile")
//   compact        bool       — omit the section header (for use inside a Section wrapper)

import { useState } from 'react';

import { NONE_OF_THESE } from '../utils/patientProfileCompleteness';

import Icon from './Icon';
const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

// ── Clinical constants (Constitution §VIII) ───────────────────────────────────
export const CONDITIONS = [
  'Type 2 Diabetes',
  'Pre-diabetes',
  'Hypertension',
  'High cholesterol',
  'Heart disease',
  'Thyroid disorder',
  'Kidney disease',
  'Liver disease',
  'PCOS / PCOD',
  'Obesity / Metabolic syndrome',
  'Asthma / COPD',
  'Cancer (any)',
];

export const FAMILY_CONDITIONS = [
  'Heart attack before age 55',
  'Stroke',
  'Type 2 Diabetes',
  'Hypertension',
  'High cholesterol',
  'Kidney disease',
  'Cancer (any)',
  'Thyroid disorder',
];

// ── Parse helpers — medicalHistory is stored as a human-readable string ────────
// "Conditions: Type 2 Diabetes, Hypertension. Family history: Stroke, Cancer (any)."
// We reverse-parse on load to pre-populate the chips.

function parseConditionsFromHistory(medHistory) {
  if (!medHistory) return { conditions: [], familyHistory: [] };
  try {
    const condMatch   = medHistory.match(/Conditions:\s*([^.]+)/i);
    const familyMatch = medHistory.match(/Family history:\s*([^.]+)/i);
    const conditions    = condMatch   ? condMatch[1].split(',').map(s => s.trim()).filter(Boolean)   : [];
    const familyHistory = familyMatch ? familyMatch[1].split(',').map(s => s.trim()).filter(Boolean) : [];
    return { conditions, familyHistory };
  } catch {
    return { conditions: [], familyHistory: [] };
  }
}

function buildMedHistory(conditions, familyHistory) {
  const parts = [];
  if (conditions.length)    parts.push(`Conditions: ${conditions.join(', ')}`);
  if (familyHistory.length) parts.push(`Family history: ${familyHistory.join(', ')}`);
  return parts.join('. ') || null;
}

// ── Shared chip style ─────────────────────────────────────────────────────────
const chipStyle = (active) => ({
  padding: '7px 14px', borderRadius: 20, cursor: 'pointer',
  fontSize: 13, fontFamily: 'var(--font-body)', fontWeight: active ? 600 : 400,
  border: `1.5px solid ${active ? 'var(--brand-dark)' : 'var(--border)'}`,
  background: active ? 'var(--brand-dark)' : 'var(--bg-surface)',
  color: active ? 'var(--text-on-brand-dark)' : 'var(--text-primary)',
  transition: 'all 0.15s',
});

const sectionLabel = {
  fontSize: 13, fontWeight: 600,
  color: 'var(--text-secondary)',
  fontFamily: 'var(--font-body)',
  marginBottom: 10, display: 'block',
};

const chipGrid = { display: 'flex', flexWrap: 'wrap', gap: 8 };

// ── Main component ─────────────────────────────────────────────────────────────
export default function HealthProfileForm({
  patient,
  initialData = {},
  onSave,
  onSkip,
  onBack,
  saveLabel = 'Save health profile',
  compact = false,
}) {
  // Pre-populate from existing patient medicalHistory string if initialData not provided
  const parsed = parseConditionsFromHistory(initialData.medicalHistory);

  const [conditions,    setConditions]    = useState(initialData.conditions    ?? parsed.conditions);
  const [familyHistory, setFamilyHistory] = useState(initialData.familyHistory ?? parsed.familyHistory);
  const [medications,   setMedications]   = useState(initialData.medications   ?? initialData.currentMedications ?? '');
  const [allergies,     setAllergies]     = useState(initialData.allergies      ?? '');
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [success,       setSuccess]       = useState('');

  // "None of these" is mutually exclusive with every real condition: picking it
  // clears the rest, and picking a real one clears it. Allowing both at once
  // would store a self-contradicting answer ("no conditions, and also
  // hypertension") that the gate would nonetheless count as complete.
  function toggle(item, list, setList) {
    setList(prev => {
      if (item === NONE_OF_THESE) {
        return prev.includes(NONE_OF_THESE) ? [] : [NONE_OF_THESE];
      }
      const withoutNone = prev.filter(x => x !== NONE_OF_THESE);
      return withoutNone.includes(item)
        ? withoutNone.filter(x => x !== item)
        : [...withoutNone, item];
    });
  }

  // Both rows must be answered before this can be saved. An untouched row is
  // now distinguishable from "nothing to declare" precisely because the latter
  // has its own chip — see utils/patientProfileCompleteness.js.
  const conditionsAnswered = conditions.length > 0;
  const familyAnswered     = familyHistory.length > 0;
  const canSave            = conditionsAnswered && familyAnswered;

  async function handleSave(e) {
    if (e?.preventDefault) e.preventDefault();

    if (!canSave) {
      setError(!conditionsAnswered && !familyAnswered
        ? 'Please answer both the conditions and family history questions — pick "None of these" if neither applies.'
        : !conditionsAnswered
          ? 'Please answer the conditions question — pick "None of these" if none apply.'
          : 'Please answer the family history question — pick "None of these" if none apply.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const medHistory = buildMedHistory(conditions, familyHistory);

    try {
      const token = localStorage.getItem('previu_token');
      const res = await fetch(`${API_BASE}/patients/${patient.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          medicalHistory:     medHistory,
          allergies:          allergies  || null,
          currentMedications: medications || null,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Save failed (${res.status})`);
      }

      const saved = { conditions, familyHistory, medications, allergies, medicalHistory: medHistory };
      setSuccess('Health profile saved successfully.');
      onSave?.(saved);
    } catch (err) {
      setError(err.message || 'Could not save. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const textareaStyle = {
    width: '100%', boxSizing: 'border-box',
    border: '1px solid var(--border)',
    borderRadius: 8, padding: '10px 12px',
    fontSize: 13, color: 'var(--text-primary)',
    fontFamily: 'var(--font-body)', resize: 'vertical',
    background: 'var(--bg-subtle)', outline: 'none',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {!compact && (
        <div>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: 'var(--brand-accent)',
            fontFamily: 'var(--font-body)', marginBottom: 6,
          }}>
            Constitution §VIII — Risk Profile
          </div>
          <p style={{
            fontSize: 14, color: 'var(--text-secondary)',
            fontFamily: 'var(--font-body)', lineHeight: 1.7, margin: 0,
          }}>
            This helps personalise your health thresholds and catch issues earlier.
            Changes take effect on your next triage report.
          </p>
        </div>
      )}

      {/* Success / error banners */}
      {success && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, fontSize: 13,
          background: 'var(--green-bg)', color: 'var(--green)',
          border: '1px solid var(--green)',
        }} role="status">
          ✓ {success}
        </div>
      )}
      {error && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, fontSize: 13,
          background: 'var(--red-bg)', color: 'var(--red)',
          border: '1px solid var(--red)',
        }} role="alert">
          <Icon name="alert" size={14} /> {error}
        </div>
      )}

      {/* ── Personal conditions ── */}
      <section>
        <div style={sectionLabel}>Do you have any of these conditions? <span style={{ color: 'var(--red)' }}>*</span></div>
        <div style={chipGrid}>
          {[...CONDITIONS, NONE_OF_THESE].map(c => (
            <button
              key={c} type="button"
              onClick={() => toggle(c, conditions, setConditions)}
              style={chipStyle(conditions.includes(c))}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* ── Family history ── */}
      <section>
        <div style={sectionLabel}>Family history (first-degree relatives) <span style={{ color: 'var(--red)' }}>*</span></div>
        <div style={chipGrid}>
          {[...FAMILY_CONDITIONS, NONE_OF_THESE].map(c => (
            <button
              key={c} type="button"
              onClick={() => toggle(c, familyHistory, setFamilyHistory)}
              style={chipStyle(familyHistory.includes(c))}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* ── Medications ── */}
      <section>
        <label style={sectionLabel} htmlFor="hpf-medications">
          Current medications (optional)
        </label>
        <textarea
          id="hpf-medications"
          value={medications}
          onChange={e => setMedications(e.target.value)}
          placeholder="e.g. Metformin 500mg, Atorvastatin 10mg"
          rows={3}
          style={textareaStyle}
        />
      </section>

      {/* ── Allergies ── */}
      <section>
        <label style={sectionLabel} htmlFor="hpf-allergies">
          Known allergies (optional)
        </label>
        <textarea
          id="hpf-allergies"
          value={allergies}
          onChange={e => setAllergies(e.target.value)}
          placeholder="e.g. Penicillin, Shellfish"
          rows={2}
          style={textareaStyle}
        />
      </section>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
        <div style={{ display: 'flex', gap: 10 }}>
          {onBack && (
            <button type="button" onClick={onBack} style={{
              flex: 1, border: '1.5px solid var(--border)', borderRadius: 10,
              padding: '12px 18px', fontSize: 14, fontWeight: 600,
              color: 'var(--text-primary)', background: 'var(--bg-surface)',
              cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}>
              ← Back
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || !canSave}
            title={canSave ? undefined : 'Answer both questions above to save'}
            style={{
              flex: onBack ? 2 : 1, border: 'none', borderRadius: 10,
              padding: '13px 18px', fontSize: 15, fontWeight: 600,
              color: 'var(--text-on-brand-dark)',
              background: (loading || !canSave) ? 'var(--text-muted)' : 'var(--brand-dark)',
              cursor: (loading || !canSave) ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-body)', opacity: (loading || !canSave) ? 0.7 : 1,
            }}
          >
            {loading ? 'Saving…' : saveLabel}
          </button>
        </div>

        {onSkip && (
          <button
            type="button"
            onClick={() => {
              localStorage.setItem('previu_onboarding_skipped', 'true');
              localStorage.removeItem('previu_onboarding_complete');
              onSkip();
            }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, color: 'var(--text-muted)', fontFamily: 'var(--font-body)',
              textAlign: 'center', padding: '4px 0',
            }}
          >
            Skip for now — I'll fill this in later
          </button>
        )}
      </div>
    </div>
  );
}
